import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { ListenButton, PremiumListenButton } from "@/components/ListenButton";
import {
  Loader2, BookOpen, CheckCircle2, XCircle, ArrowRight, GraduationCap, Trophy,
} from "lucide-react";

const STEPS = ["Lesson", "10 questions", "Done"];

const ProgressBar = ({ active }) => (
  <div className="flex items-center gap-2 mb-8" data-testid="plan-progress">
    {STEPS.map((label, i) => (
      <div key={label} className="flex items-center gap-2 flex-1">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
          i === active ? "bg-primary text-primary-foreground"
            : i < active ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground"
        }`}>
          {i < active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-4 text-center">{i + 1}</span>}
          {label}
        </div>
        {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < active ? "bg-success" : "bg-border"}`} />}
      </div>
    ))}
  </div>
);

export default function PlanTodayPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { isPro } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("lesson"); // lesson | questions | done
  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null); // {is_correct, correct_option_id, explanation}
  const [done, setDone] = useState(null); // {score, total, next_topic_name, subject_name}
  const startedTracked = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        const subject = sp.get("subject");
        const d = await api.get(`/plan/today${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`);
        setData(d);
        const answered = Object.keys(d.answers || {}).length;
        if (d.current_step === "done" || (answered >= (d.questions?.length || 0) && (d.questions?.length || 0) > 0)) {
          const fin = await api.post(`/plan/${d.session_id}/complete`, {});
          setDone(fin);
          setPhase("done");
        } else if (d.current_step === "questions") {
          setQIndex(Math.min(answered, (d.questions?.length || 1) - 1));
          setPhase("questions");
        } else {
          setPhase("lesson");
        }
        if (!startedTracked.current && d.current_step === "lesson" && answered === 0) {
          trackEvent("plan_started", { subject_id: d.subject_id, topic_id: d.topic_id });
          startedTracked.current = true;
        }
      } catch (e) {
        // Feature off or not available -> back to dashboard
        navigate("/dashboard", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startQuestions = async () => {
    await api.post(`/plan/${data.session_id}/lesson-complete`, {});
    trackEvent("plan_lesson_completed", { subject_id: data.subject_id, topic_id: data.topic_id });
    setPhase("questions");
    setQIndex(0);
  };

  const checkAnswer = async () => {
    if (!selected) return;
    const q = data.questions[qIndex];
    const r = await api.post(`/plan/${data.session_id}/answer`, {
      question_id: q.id, selected_option_id: selected,
    });
    setResult(r);
  };

  const nextQuestion = async () => {
    if (qIndex + 1 >= data.questions.length) {
      const fin = await api.post(`/plan/${data.session_id}/complete`, {});
      trackEvent("plan_set_completed", { score: fin.score, subject_id: data.subject_id });
      setDone(fin);
      setPhase("done");
      return;
    }
    setQIndex(qIndex + 1);
    setSelected(null);
    setResult(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!data) return null;

  const lesson = data.lesson;
  const activeStep = phase === "lesson" ? 0 : phase === "questions" ? 1 : 2;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="plan-today-page">
      <div className="mb-2 text-sm text-muted-foreground">Today&apos;s 10-minute plan · {data.subject_name}</div>
      <ProgressBar active={activeStep} />

      {/* LESSON */}
      {phase === "lesson" && (
        <div data-testid="plan-lesson">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><BookOpen className="w-6 h-6" /></div>
            <div>
              <h1 className="text-xl font-bold">{lesson?.name || "Your lesson"}</h1>
              <div className="text-sm text-muted-foreground">Read this, then try 10 questions</div>
            </div>
          </div>
          {lesson && (lesson.sections || []).length > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <ListenButton text={(lesson.sections || []).map((s) => `${s.title}. ${s.content}`).join(" ")} label="Listen to lesson" />
              {isPro && <PremiumListenButton text={(lesson.sections || []).map((s) => `${s.title}. ${s.content}`).join(" ")} />}
            </div>
          )}
          <div className="space-y-5">
            {(lesson?.sections || []).map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4" data-testid={`plan-lesson-section-${i}`}>
                {s.title && <div className="font-semibold mb-1">{s.title}</div>}
                <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{s.content}</div>
              </div>
            ))}
            {(!lesson || (lesson.sections || []).length === 0) && (
              <div className="text-sm text-muted-foreground">No lesson content for this topic yet — jump straight to the questions.</div>
            )}
          </div>
          <button onClick={startQuestions} data-testid="plan-start-questions-btn" className="mt-6 w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium flex items-center justify-center gap-2">
            Start the 10 questions <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* QUESTIONS */}
      {phase === "questions" && data.questions[qIndex] && (
        <div data-testid="plan-questions">
          <div className="text-sm text-muted-foreground mb-2">Question {qIndex + 1} of {data.questions.length}</div>
          {data.questions[qIndex].clinical_vignette && (
            <div className="rounded-xl bg-secondary/50 border border-border p-3 text-sm mb-3 whitespace-pre-wrap">{data.questions[qIndex].clinical_vignette}</div>
          )}
          <h2 className="text-lg font-semibold mb-4" data-testid="plan-question-text">{data.questions[qIndex].question_text}</h2>
          <div className="space-y-2">
            {data.questions[qIndex].options.map((o) => {
              const isSel = selected === o.id;
              const isCorrect = result && o.id === result.correct_option_id;
              const isWrongPick = result && isSel && !result.is_correct;
              return (
                <button
                  key={o.id}
                  disabled={!!result}
                  data-testid={`plan-option-${o.id}`}
                  onClick={() => setSelected(o.id)}
                  className={`w-full text-left p-3 rounded-xl border text-sm transition ${
                    isCorrect ? "border-success bg-success/10"
                      : isWrongPick ? "border-destructive bg-destructive/10"
                      : isSel ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    {isCorrect && <CheckCircle2 className="w-4 h-4 text-success" />}
                    {isWrongPick && <XCircle className="w-4 h-4 text-destructive" />}
                    {o.text}
                  </span>
                </button>
              );
            })}
          </div>
          {result && (
            <div className="mt-4 rounded-xl border border-border bg-card p-4" data-testid="plan-explanation">
              <div className={`font-semibold mb-1 ${result.is_correct ? "text-success" : "text-destructive"}`}>
                {result.is_correct ? "Correct" : "Not quite"}
              </div>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap">{result.explanation}</div>
            </div>
          )}
          {!result ? (
            <button onClick={checkAnswer} disabled={!selected} data-testid="plan-check-btn" className="mt-6 w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-50">Check answer</button>
          ) : (
            <button onClick={nextQuestion} data-testid="plan-next-btn" className="mt-6 w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium flex items-center justify-center gap-2">
              {qIndex + 1 >= data.questions.length ? "See my result" : "Next question"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* DONE */}
      {phase === "done" && done && (
        <div className="text-center" data-testid="plan-done">
          <div className="w-16 h-16 rounded-2xl bg-success/15 text-success flex items-center justify-center mx-auto mb-4"><Trophy className="w-8 h-8" /></div>
          <h1 className="text-2xl font-bold mb-1">You scored {done.score}/{done.total}</h1>
          <p className="text-muted-foreground mb-6 flex items-center justify-center gap-2">
            <GraduationCap className="w-4 h-4" />
            {done.next_topic_name
              ? `Tomorrow: your Day 2 set on ${done.next_topic_name} (10 questions)`
              : "Come back tomorrow for your Day 2 set (10 questions)"}
          </p>
          <button onClick={() => navigate("/dashboard")} data-testid="plan-dashboard-btn" className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium">
            Go to my dashboard
          </button>
        </div>
      )}
    </div>
  );
}
